import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@auth0/auth0-angular';
import { ThemeService } from '../../core/services/theme.service';
import { ProfileService, UserProfile } from '../../core/services/profile.service';
import { RolesService } from '../../core/auth/roles.service';
import { BillingService } from '../../core/services/billing.service';
import { LoggingService } from '../../core/services/logging.service';

@Component({
    standalone: true,
    selector: 'app-profile-page',
    imports: [
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: './profile-page.component.html',
    styleUrl: './profile-page.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePageComponent implements OnInit {
  profileForm: FormGroup;
  isLoading = signal(false);
  isSaving = signal(false);
  saveSuccess = signal(false);
  isManagingSubscription = signal(false);
  userProfile = signal<UserProfile | null>(null);
  userEmail = signal<string>('');

  // Services
  private auth = inject(AuthService);
  private themeService = inject(ThemeService);
  private profileService = inject(ProfileService);
  private billingService = inject(BillingService);
  private logger = inject(LoggingService);
  public readonly rolesService = inject(RolesService);

  constructor(private fb: FormBuilder) {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      themePreference: ['dark', Validators.required],
      marketingConsent: [false]
    });
  }

  ngOnInit(): void {
    // Load user profile when component initializes
    this.loadUserProfile();
  }

  private loadUserProfile(): void {
    this.isLoading.set(true);

    // Check if user is authenticated first
    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        // Subscribe to user observable
        this.auth.user$.subscribe(user => {
          if (user?.name) {
            this.userEmail.set(user.name);
          }
          if (user?.sub) {
            // Load profile from service
            this.profileService.getUserProfile().subscribe({
              next: (profile) => {
                if (profile) {
                  this.userProfile.set(profile);
                  this.profileForm.patchValue({
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    marketingConsent: profile.marketingConsent || false
                    // themePreference:
                  });
                }
                this.isLoading.set(false);
              },
              error: (error) => {
                this.logger.error('Error loading profile:', error);
                this.isLoading.set(false);
              }
            });
          } else {
            // User object not available yet
            this.isLoading.set(false);
          }
        });
      } else {
        // User not authenticated
        this.isLoading.set(false);
      }
    });
  }

  onSubmit(): void {
    if (this.profileForm.valid) {
      this.isSaving.set(true);
      this.saveSuccess.set(false);

      // Check authentication and get user
      this.auth.isAuthenticated$.subscribe(isAuthenticated => {
        if (isAuthenticated) {
          this.auth.user$.subscribe(user => {
            if (user?.sub) {
              const formValue = this.profileForm.value;
              this.profileService.updateUserProfile({
                firstName: formValue.firstName,
                lastName: formValue.lastName,
                marketingConsent: formValue.marketingConsent
              }).subscribe({
                next: (updatedProfile) => {
                  if (updatedProfile) {
                    this.userProfile.set(updatedProfile);
                    this.saveSuccess.set(true);

                    // Update theme if changed
                    if (formValue.themePreference !== this.themeService.currentTheme()) {
                      this.themeService.setTheme(formValue.themePreference);
                    }

                    // Hide success message after 3 seconds
                    setTimeout(() => this.saveSuccess.set(false), 3000);
                  }
                  this.isSaving.set(false);
                },
                error: (error) => {
                  this.logger.error('Error saving profile:', error);
                  this.isSaving.set(false);
                }
              });
            } else {
              this.isSaving.set(false);
            }
          });
        } else {
          this.isSaving.set(false);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.profileForm.markAllAsTouched();
    }
  }

  onThemeChange(theme: 'light' | 'dark'): void {
    this.profileForm.patchValue({ themePreference: theme });
    this.themeService.setTheme(theme);
  }

  onManageSubscription(): void {
    this.isManagingSubscription.set(true);

    this.billingService.createCustomerPortalSession().subscribe({
      next: (response) => {
        window.location.assign(response.url);
      },
      error: () => {
        this.isManagingSubscription.set(false);
      }
    });
  }
}
